import { Fragment, type JSX, type ReactNode } from "react";

import { type WorkoutItemLayout } from "./WorkoutEditStore";

export interface RenderGroup {
  id: string;
  item: WorkoutItemLayout;
  level: number;
  children: ReactNode;
  childCount: number;
  index: number;
  isLast: boolean;
  previousItemId?: string;
}

export interface RenderNormal {
  id: string;
  item: WorkoutItemLayout;
  level: number;
  index: number;
  isLast: boolean;
  previousItemId?: string;
}

export interface RenderInsertBetweenItems {
  kind: "between-items";
  index: number;
  parentId: string | null;
  previousItemId: string | undefined;
  itemId: string;
}

export interface RenderInsertRoot {
  kind: "root";
  parentId: string | null;
}

export type RenderInsert = RenderInsertBetweenItems | RenderInsertRoot;

interface BaseWorkoutItemsListProps {
  items: WorkoutItemLayout[];
  renderGroup: (args: RenderGroup) => JSX.Element;
  renderNormal: (args: RenderNormal) => JSX.Element;
  renderInsert?: (args: RenderInsert) => JSX.Element | null;
  isSuperset?: (item: WorkoutItemLayout) => boolean;
  shouldSkipItem?: (item: WorkoutItemLayout) => boolean;
  getPreviousItemId?: (
    prevItem: WorkoutItemLayout | null,
  ) => string | undefined;
}

export function BaseWorkoutItemsList(props: BaseWorkoutItemsListProps) {
  function renderItemType(
    item: WorkoutItemLayout,
    level: number,
    index: number,
    isLast: boolean,
    parentId: string | null,
    previousItemId?: string,
  ): JSX.Element {
    if (props.shouldSkipItem?.(item)) {
      return <Fragment key={item.workoutItemId} />;
    }

    const insertElement =
      props.renderInsert && previousItemId
        ? props.renderInsert({
            kind: "between-items",
            index: index,
            parentId,
            previousItemId: previousItemId,
            itemId: item.workoutItemId,
          })
        : null;

    if (item.type === "GROUP") {
      const mappedChildren =
        item.children?.map((child, childIndex, array) => {
          const prevChild = childIndex > 0 ? array[childIndex - 1] : null;
          const prevChildId = props.getPreviousItemId?.(prevChild);
          return renderItemType(
            child,
            level + 1,
            childIndex,
            childIndex === array.length - 1,
            item.workoutItemId,
            prevChildId,
          );
        }) ?? [];

      const groupRootInsert = props.renderInsert
        ? props.renderInsert({
            kind: "root",
            parentId: item.workoutItemId,
          })
        : null;

      const children = groupRootInsert
        ? [
            ...mappedChildren,
            <Fragment key={`${item.workoutItemId}-root-insert`}>
              {groupRootInsert}
            </Fragment>,
          ]
        : mappedChildren;

      return (
        <Fragment key={item.workoutItemId}>
          {insertElement}
          {props.renderGroup({
            id: item.workoutItemId,
            item,
            level,
            children,
            index,
            childCount: children.length,
            isLast,
            previousItemId,
          })}
        </Fragment>
      );
    } else if (item.type === "NORMAL") {
      return (
        <Fragment key={item.workoutItemId}>
          {insertElement}
          {props.renderNormal({
            id: item.workoutItemId,
            item,
            level,
            index,
            isLast,
            previousItemId,
          })}
        </Fragment>
      );
    }

    return <Fragment key={item.workoutItemId} />;
  }

  const rootInsertElement = props.renderInsert
    ? props.renderInsert({ kind: "root", parentId: null })
    : null;

  return (
    <>
      {props.items.map((item, index, array) => {
        const prevItem = index > 0 ? array[index - 1] : null;
        const prevItemId = props.getPreviousItemId?.(prevItem);
        return renderItemType(
          item,
          0,
          index,
          index === array.length - 1,
          null,
          prevItemId,
        );
      })}
      {rootInsertElement}
    </>
  );
}
